import { useContext, useEffect, useRef, useState } from "react";
import Call from "../../../models/Call";
import { LeadListMode } from "../../../models/LeadListMode";
import { sleep } from "../../../utils";
import { createEasyTelecomOutboundCall, createVertoLogin, forceHangUpEasyTelecomCall, updatePredictiveModeUser, cohearCall as cohearCallService, cohearUser as cohearUserService, VertoLogin, answerInboundEasyTelecomCall, updateUserCohearingParkedCall, createVertoLoginAsDetector } from "./service";
import { CallMetadata, HangUpOptions, TelephonyServiceCallInfo, TelephonyServiceProps } from "../TelephonyService";
import { TelephonyContext } from "../TelephonyContext";
import { startPredictiveMode as startPredictiveModeService } from "../LeadListModeList/service";
import { CallDisconnectReason } from "../../../models/CallDisconnectReason";
import { IncomingCall } from "../../../models/IncomingCall";
import { TelephonyContextType, TelephonySectionType } from "../TelephonyContext";
import { Project } from "../../../models/Project";
import { ProjectContext } from "../../users/ProjectContextProvider";
import { waitFor } from "../../../utils/async";
import { addConsoleListener, ConsoleListener, removeConsoleListener } from "../../../log_monitor";
import { AsynchronousNotificationContext } from "../../../AsynchronousNotificationContextProvider";
import { AsynchronousNotification } from "../../../models/AsynchronousNotification";
import { text } from "../../../utils/i18n";
import moment from "moment";
import { useCurrentUser, useOrganization } from "../../user/UserDispatcher";
import { Feature } from "../../../models/Feature";
import { AppType, appType } from "../../../AppType";
import { getCurrentDetector } from "../../../components_telephony_detector/service";

/// <reference path="verto-jquery.d.ts" />

function appendScriptTag(src: string) {
  if (document.head.querySelector(`script[src="${src}"]`)) {
    //  The script has loaded once. If we remove the script or re-load it, then the dial manager can break.
    return;
  }

  const element = document.createElement('script');
  element.src = src;

  document.head.appendChild(element);
}

const parkRecipient = 'park';

function getEasyTelecomCallId(call: VertoCall | null): string | null {
  const callId = call?.params.variables?.verto_svar_api_callid || null;

  //  In case EasyTelecom's ID would be assigned as callID.
  //  Example real ID at time of writing: c22254a5no4rq-g6pmclc-3c566ak-dgkk
  if (!callId && /^\w{13}-\w{7}-\w{7}-\w{4}$/.test(call?.callID)) {
    return call.callID;
  }

  return callId;
}

function getVertoCallId(call: VertoCall | null): string {
  return getEasyTelecomCallId(call) || call?.callID;
}

/**
 * Handles calls using EasyTelecom.
 * Requires jQuery and verto-min dependencies.
 */
export const EasyTelecomTelephonyService: React.FC<TelephonyServiceProps> = ({
  _ref,
  onCallConnected,
  onCallConnecting,
  onCallError: onCallConnectionError,
  onCallDisconnected,
  onServiceDisconnected,
  onServiceError,
  onServiceConnected,
  onRemoteStream
}) => {
  const context = useContext(TelephonyContext); //  Null i detektor-läge.
  const contextRef = useRef<TelephonyContextType>(context);
  contextRef.current = context;
  const currentUser = useCurrentUser(); //  Null i detektor-läge.
  const organization = useOrganization(); //  Null i detektor-läge.
  const preferences = currentUser?.getPreferences(organization);

  const debug = useRef<string[]>([]); // Debug lines to save to the current call.
  const debugListener = useRef<ConsoleListener>(null); // Console listener which adds to the debug list.

  const vertoCallRef = useRef<(VertoCall & { recovered?: boolean; }) | null>(null);
  const vertoRef = useRef<$.verto | null>(null);
  const asyncNotifications = useContext(AsynchronousNotificationContext);
  const recoverNotificationCount = useRef(0);
  const recoverNotificationAt = useRef<Date>(null);
  const purgeOnCallDisconnectedTimeout = useRef<any>(null);
  const [isCreatePredictiveParkedCallNow, setCreatePredictiveParkedCallNow] = useState(false);

  const currentProject = useRef<Project>(null);
  currentProject.current = useContext(ProjectContext)?.currentProject; // Can be null in express flows.

  useEffect(() => {
    if (!isCreatePredictiveParkedCallNow) {
      return;
    }

    if (organization.hasFeature(Feature.Offer) && (
      currentProject.current?.defaultUserPreferences?.combineOfferContactView ??
      organization.defaultUserPreferences.combineOfferContactView
    ) && context.callSessions.length) {
      return;
    }

    refreshPredictiveModeParkedCall();
    setCreatePredictiveParkedCallNow(false);

  }, [isCreatePredictiveParkedCallNow, context?.callSessions.length]);

  const destinationPhoneNumber = useRef<string | null>(null);

  /**
   * Whether to create a new predictive mode parked call when the current call is destroyed.
   */
  const shouldCreateParkedCallOnDestroy = useRef(false);
  const streamElement = useRef<HTMLAudioElement>(null);

  /**
   * Callback after parked call becomes active.
   * 
   * The can only be 1 callback since there can only be 1 parked call at a time,
   * and this is more safe than using Promises or array of callbacks.
   */
  const onParkedCallActive = useRef<((call: VertoCall, apiCallId: string) => any) | null>(null);

  useEffect(() => {
    if (!context) {
      return;
    }

    if (context.leadListMode === LeadListMode.Predictive) {
      //  During predictive mode.
      //  When this call is destroyed, create a new parked call.
      shouldCreateParkedCallOnDestroy.current = true;
    } else if (context.cohearing.user?.isActive) {
      //  While cohearing a user.
      shouldCreateParkedCallOnDestroy.current = true;
    } else if (context.cohearing.user?.isBusy) {
      //  In case the other call disconnects before isActive becomes true.
      shouldCreateParkedCallOnDestroy.current = true;
    } else if (!context.cohearing.user?.isBusy && !context.cohearing.user?.isActive) {
      //  When not in user cohearing mode, be sure to not create a new parked call.
      shouldCreateParkedCallOnDestroy.current = false;
    }
  }, [context?.leadListMode, context?.cohearing.user?.isActive, context?.cohearing.user?.isBusy]);

  function getCallInfo(call: VertoCall | null): TelephonyServiceCallInfo {
    let contactPhoneNumber = call?.params.remote_caller_id_number;
    if (contactPhoneNumber === parkRecipient) {
      contactPhoneNumber = destinationPhoneNumber.current;
    }

    const ref = vertoCallRef.current?.callID === call?.callID ?
      vertoCallRef.current : null;

    return {
      contactPhoneNumber,
      contactFields: null,
      callerPhoneNumber: null,
      contactId: null,
      easyTelecomUserCallId: getVertoCallId(call),
      isRecovered: ref?.recovered
    };
  }

  async function createAndBridgeCall(easyTelecomUserCallId: string, callerPhoneNumber: string, destinationPhoneNumber: string, metadata?: CallMetadata) {
    try {
      await createEasyTelecomOutboundCall(
        easyTelecomUserCallId,
        callerPhoneNumber,
        destinationPhoneNumber,
        metadata,
        debug.current
      );
    } catch (_error) {
      const error = _error as any;
      const cause: string | undefined = error?.cause?.error?.reason || error?.localizedMessage;

      onCallConnectionError(cause, error);
    }
  }

  async function refreshUserCohearingParkedCall() {
    onParkedCallActive.current = async (call, callId) => {
      for (let attempt = 1; contextRef.current.cohearing.user?.isActive; attempt++) {
        try {
          await updateUserCohearingParkedCall(callId);
          break;
        } catch (error) {
          console.error("alert: Error updating parked call for cohearing user", error);

          if (attempt >= 3) {
            console.error("Too many failed attempts to update parked call for cohearing user, ending cohearing session.");
            contextRef.current.endCohearing();
            break;
          } else {
            await sleep(2000);
          }
        }
      }
    };

    await createParkedCall();
  }

  async function refreshPredictiveModeParkedCall() {
    onParkedCallActive.current = async (call, callId) => {
      console.debug("Updating parked call to %s for routing predictive calls", callId);

      for (let attempt = 1; contextRef.current.leadListMode === LeadListMode.Predictive && contextRef.current.leadList?.id; attempt++) {
        try {
          await updatePredictiveModeUser(contextRef.current.leadList?.id, {
            projectId: currentProject.current?.id,
            easyTelecomUserCallId: callId
          });
          break;
        } catch (error) {
          console.error("alert: Error updating parked call for predictive mode", error);

          if (attempt >= 3) {
            console.error("Too many failed attempts to update parked call for predictive mode, ending.");
            contextRef.current.setState({
              leadList: null,
              leadListMode: null,
              section: TelephonySectionType.Standard,
            });
            break;
          } else {
            await sleep(2000);
          }
        }
      }
    }

    await createParkedCall();
  }

  /**
   * Initializes Verto, a softphone client.
   */
  async function initService() {
    try {
      console.debug("Initializing service");

      //  Prevent reusing an old verto session.
      sessionStorage.removeItem('verto_session_uuid');
      localStorage.removeItem('verto_session_uuid');

      appendScriptTag("/libs/easytelecom/jquery-2.1.1.min.js");
      while (!window['jQuery']) {
        await sleep(10);
      }
      console.debug("window.jQuery OK");

      appendScriptTag("/libs/easytelecom/jquery.json-2.4.min.js");
      if (!window.$['toJSON']) {
        await sleep(10);
      }
      console.debug("window.$.toJSON OK");

      appendScriptTag("/libs/easytelecom/verto-min-0.0.2.js");
      while (!window.$.verto) {
        await sleep(10);
      }
      console.debug("window.$.verto OK");

      const vertoEnums: VertoEnums = $.verto['enum'];

      streamElement.current = document.createElement('audio');
      streamElement.current.autoplay = true;
      streamElement.current.hidden = true;
      streamElement.current.id = 'easytelecom-stream';
      document.body.appendChild(streamElement.current);

      let login: VertoLogin;
      while (true) {
        try {
          if (appType === AppType.TelephonyDetector) {
            login = await createVertoLoginAsDetector();

          } else {
            login = await createVertoLogin();
          }
          break;
        } catch (error) {
          const previousError = contextRef.current?.serviceError;
          const isNewError = !previousError ||
            JSON.stringify({ ...error, message: error.message, name: error.message }) !==
            JSON.stringify({ ...previousError, message: previousError.message, name: previousError.message });

          //  If we get an error, retry forever, unless the tab is hidden.
          if (!error.retryable && isNewError) {
            //  Notify developers.
            console.error("alert: Error creating verto login", error);
          } else if (!error.retryable) {
            console.error("Error creating verto login", error);
          }

          onServiceError(error);

          while (document.hidden) {
            //  Do not re-attempt if document is hidden.
            await sleep(1e3);
          }

          await sleep(5e3);
        }
      }

      const verto = vertoRef.current = new $.verto({
        login: `${login.username}@${login.domain}`,
        passwd: login.password,
        iceServers: true,
        socketUrl: login.url,
        tag: streamElement.current.id
      }, {
        onRemoteStream: stream => {
          onRemoteStream?.(stream);
        },
        onWSClose: () => {
          console.debug("Logged out with Verto");
          onServiceDisconnected();
        },
        onMessage: (verto, call, type) => {
          console.debug("Verto message:", type);

          if (type === vertoEnums.message.clientReady) {
            console.info("Verto client is ready");
            onServiceConnected();
          } else if (type === vertoEnums.message.display) {
            destinationPhoneNumber.current = call.params.remote_caller_id_number;

            console.log("Bridged with: %s", call.params.variables?.verto_svar_api_callid);
            onCallConnected(getCallInfo(call));
          }

          if (type === vertoEnums.message.clientReady && appType === AppType.TelephonyDetector) {
            createParkedCall();
          }
        },
        onDialogState: call => {
          vertoCallRef.current = call;

          //  Ifall useEffect inte kör.
          if (call && appType === AppType.TelephonyDetector) {
            call.setMute("off");

          } else {
            call.setMute(contextRef.current.muteReason ? "off" : "on");
          }

          console.debug("Verto: New Verto call state for %s:", call.callID, call.state, {
            //  Log useful stuff here. Logging the entire call will be too much data sent to the backend.
            answered: call.answered,
            attach: call.attach,
            params: call.params,
            cause: call.cause,
            direction: call.direction,
            gotAnswer: call.gotAnswer,
            useMic: call.useMic,
            useSpeak: call.useSpeak,
            lastState: call.lastState
          });

          if (call.state === vertoEnums.state.destroy) {
            const callback = () => {
              //  Should be invoked when co-hearing.
              onCallDisconnected(getCallInfo(call));

              destinationPhoneNumber.current = null;
            }

            if (call.lastState === vertoEnums.state.purge) {
              //  Purge event (see verto-client):
              //  1. on websocket close
              //  2. on logout
              //  3. on punt (?)

              //  Delay onCallDisconnected, wait for recovering state.
              purgeOnCallDisconnectedTimeout.current = setTimeout(() => {
                callback();
                purgeOnCallDisconnectedTimeout.current = null;
              }, 15e3);

            } else {
              callback();
            }

          } else if (call.state === vertoEnums.state.recovering) {

            //  Limit number of notifications in case this happens often.
            if (recoverNotificationCount.current < 3 && (
              !recoverNotificationAt.current || recoverNotificationAt.current < moment().subtract(10, 'minutes').toDate()
            )) {
              asyncNotifications.notify(new AsynchronousNotification({
                id: "CallRecovered",
                title: text`Avbröts samtalet?`,
                icon: { name: "phone" },
                //  Håll det kort eftersom man är i ett samtal och behöver fokusera på två saker samtidigt.
                body: text`Kontrollera anslutning och att datorn inte hamnar i viloläge under samtalet.`,
                actions: {
                  default: {
                    chevron: false,
                    onClick() {
                      return true;
                    }
                  }
                }
              }));

              recoverNotificationAt.current = new Date();
              recoverNotificationCount.current++;
            }

            clearTimeout(purgeOnCallDisconnectedTimeout.current);
            purgeOnCallDisconnectedTimeout.current = null;

            if (vertoCallRef.current) {
              vertoCallRef.current.recovered = true;
            }
          }

          if (call.state === vertoEnums.state.destroy &&
            appType === AppType.TelephonyDetector) {
            createParkedCall(); //  Håll aktiv.
            return;
          }

          if (call.state === vertoEnums.state.destroy &&
            contextRef.current.cohearing.user?.isActive &&
            shouldCreateParkedCallOnDestroy.current) {
            refreshUserCohearingParkedCall();

          } else if (call.state === vertoEnums.state.destroy &&
            contextRef.current.leadListMode === LeadListMode.Predictive &&
            shouldCreateParkedCallOnDestroy.current) {
            setCreatePredictiveParkedCallNow(true);
          }

          if (call.state !== vertoEnums.state.active) {
            return;
          }

          const easyTelecomUserCallId = getEasyTelecomCallId(call);
          if (!easyTelecomUserCallId) {
            //  Next state should contain EasyTelecom's call ID.
            console.warn("Verto call %s is missing EasyTelecom ID on active state", call.callID);
            return;
          }

          onParkedCallActive.current?.(call, easyTelecomUserCallId);
          onParkedCallActive.current = null;
        },
        onEvent: (verto, event, data) => {
          console.debug("Verto event:", event, data);
        },
        onWSLogin: () => {
          console.debug("Logged in with Verto");
        }
      });

      verto.login();
    } catch (error) {
      console.error("Could not initialize Verto", error);
      onServiceError(error);
    }
  }

  async function destroyService() {
    streamElement.current?.remove();
  }

  /**
   * Makes a parked call using the Verto softphone.
   * To call a destination number, we:
   * 
   * 1. Call "park" on our softphone
   * 2. Create an outbound call using the EasyTelecom API and instruct the call to be parked, only if phoneNumber is non-null
   * 3. Bridge the softphone call and API call, again using the EasyTelecom API, only if phoneNumber is non-null
   */
  async function createParkedCall() {
    console.log("Creating parked call");

    appendScriptTag('/libs/easytelecom/media-device-id.min.js');

    while (!window['assureMediaInputId']) {
      // This property is required by the verto client and set in lib loaded above.
      await sleep(100);
    }

    destinationPhoneNumber.current = null;

    const previousCall = vertoCallRef.current;

    await waitFor(() => vertoRef.current, { message: "Timeout waiting for verto" });

    const verto = vertoRef.current;

    //  Have at most 1 call at a time.
    try {
      console.log("Verto: Hanging up previous verto call %s before making a new one", previousCall?.callID || '<any>');
      verto.hangup();
    } catch (error) {
      console.error("Verto: Error hanging up previous call %s", previousCall?.callID, error);
    }

    let inputDeviceName = localStorage.getItem("inputDevice") || null;
    let outputDeviceName = localStorage.getItem("outputDevice") || null;
    let inputDeviceId: string = null;
    let outputDeviceId: string = null;

    if (inputDeviceName || outputDeviceName) {
      //  Check that the devices exist.
      try {
        const devices = await window.navigator.mediaDevices.enumerateDevices();

        const inputDevice = inputDeviceName ? devices.find(device => device.label === inputDeviceName) : null;
        inputDeviceId = inputDevice?.deviceId;

        const outputDevice = outputDeviceName ? devices.find(device => device.label === outputDeviceName) : null;
        outputDeviceId = outputDevice?.deviceId;
      } catch (error) {
        console.error("Error checking input/output device", error);
      }
    }

    console.log("Current input device: %s, output device: %s", inputDeviceId, outputDeviceId);

    //  Log input devices for troubleshooting. Maybe the devices changed since the start.
    try {
      const devices = await window.navigator.mediaDevices.enumerateDevices()
        .then(devices => devices
          .filter(device => device.kind === "audioinput" || device.kind === "audiooutput"));

      console.log("Audio devices:", devices)
    } catch (error) {
      console.error("Error logging input devices for troubleshooting", error);
    }

    const call = verto.newCall({
      caller_id_name: null,
      caller_id_number: null,
      destination_number: "park",
      useStereo: false,
      useVideo: false,
      useMic: inputDeviceId ?? "any",
      useSpeak: outputDeviceId ?? "any"
    });

    console.debug("Verto: Created call %s", call.callID)

    return { parkedCall: call };
  }

  function hangUp(options?: HangUpOptions) {
    console.log("Verto: Hanging up verto call %s", vertoCallRef.current?.callID);

    const shouldCreateNewParkedCall = options?.reason !== CallDisconnectReason.UserLeavePredictiveQueue &&
      options?.reason !== CallDisconnectReason.UserAnswerInboundCall &&
      options?.reason !== CallDisconnectReason.UserEndCohearing;

    if (!shouldCreateNewParkedCall) {
      //  Do this before calling hangup() since onDialogState executes synchronously.
      shouldCreateParkedCallOnDestroy.current = false;
      setCreatePredictiveParkedCallNow(false);
    }

    const vertoEnums: VertoEnums = $.verto['enum'];

    if (vertoCallRef.current?.state === vertoEnums.state.destroy) {
      //  Already destroyed, for instance after a network issue.
      onCallDisconnected(getCallInfo(vertoCallRef.current))

    } else {
      vertoRef.current.hangup();
    }

    const callId = getVertoCallId(vertoCallRef.current);

    //  UserEndCohearing: verto.hangUp is enough.
    if (options?.reason !== CallDisconnectReason.UserEndCohearing) {
      forceHangUpEasyTelecomCall(callId, options?.reason).catch(error => {
        console.error("Could not hang-up call %s", callId, error)
      });
    }
  }

  async function cohearCall(call: Call) {
    return new Promise<string>((resolve, reject) => {
      onParkedCallActive.current = (parkedCall, parkedCallId) => {
        cohearCallService(call.id, parkedCallId)
        resolve(parkedCallId);
      };

      createParkedCall().catch(reject);
    });
  }

  async function cohearUser(userId: string) {
    onParkedCallActive.current = (parkedCall, parkedCallId) => cohearUserService(userId, parkedCallId);
    await createParkedCall();
  }

  function answerCall(call: IncomingCall) {
    return new Promise<void>(async (resolve, reject) => {
      onParkedCallActive.current = (parkedCall, parkedCallId) =>
        answerInboundEasyTelecomCall(call.id, parkedCallId)
          .then(() => resolve())
          .catch(reject);

      await createParkedCall().catch(reject);
    })
  }

  async function startPredictiveMode(leadListId: string) {
    onParkedCallActive.current = async (parkedCall, parkedCallId) => {
      //  Are we still in predictive mode or did we quickly exit?
      if (contextRef.current?.leadListMode === LeadListMode.Predictive) {
        await startPredictiveModeService(leadListId);
        await updatePredictiveModeUser(leadListId, {
          easyTelecomUserCallId: parkedCallId,
          projectId: currentProject.current?.id
        });
      }
    }

    await createParkedCall();
  }

  //  Som en "fail safe", kolla ifall detectorn verkligen är aktiv i backend, skapa annars ett nytt parkerat samtal.
  async function checkDetectorParkedCallActive() {
    const vertoEnums: VertoEnums = $.verto['enum'];

    if (vertoCallRef.current?.state !== vertoEnums.state.active) {
      return;
    }

    const detector = await getCurrentDetector();
    if (!detector?.detectorCallId) {
      createParkedCall();
    }
  }

  useEffect(() => {
    if (appType !== AppType.TelephonyDetector) {
      return;
    }

    const id = setInterval(() => checkDetectorParkedCallActive(), 10e3);

    return () => { clearInterval(id); }
  }, []);

  useEffect(() => {
    if (vertoCallRef.current && appType === AppType.TelephonyDetector) {
      try {
        //  Ska vara muted.
        vertoCallRef.current.setMute("off");
      } catch (error) {
        console.error("alert: Could not mute microphone", error);
      }

    } else if (vertoCallRef.current) {
      try {
        vertoCallRef.current.setMute(context?.muteReason ? "off" : "on");
      } catch (error) {
        console.error("Could not set mute state to:", Boolean(context?.muteReason), error);
      }
    }
  }, [Boolean(context?.muteReason), vertoCallRef.current?.callID]);

  useEffect(() => {
    _ref({
      call: (phoneNumber, caller, metadata) => {
        return new Promise(async (resolve, reject) => {
          debug.current = [];
          removeConsoleListener(debugListener.current);

          debugListener.current = getDebugListener(log =>
            debug.current = [...debug.current, log]);
          addConsoleListener(debugListener.current);

          await createParkedCall();
          destinationPhoneNumber.current = phoneNumber;

          onParkedCallActive.current = (call, apiCallId) => {
            console.debug("Calling %s via Verto", destinationPhoneNumber.current);
            createAndBridgeCall(apiCallId, caller.phoneNumber, destinationPhoneNumber.current, metadata)
              .then(resolve)
              .catch(reject);

            removeConsoleListener(debugListener.current);

            onCallConnecting(getCallInfo(call));
          };
        });
      },
      cohearCall,
      cohearUser,
      hangUp,
      sendDigit: digit => {
        vertoCallRef.current.dtmf(digit);
        console.log("Sent DTMF: %s", digit);
      },
      startPredictiveMode,
      answerCall
    });

    initService();

    return () => {
      destroyService();
    };
  }, []);

  return null;
}

function getDebugListener(callback: (log: string) => void): ConsoleListener {
  const regex = [
    /^m=audio.+/gm, //  Port information. Example: m=audio 10402 UDP/TLS/RTP/SAVPF 8 126
    /^a=candidate.+/gm, //  Port information. Example: a=candidate:KZHi6fxw2rVySuVV 1 UDP 2130706431 192.165.225.56 10402 typ host
  ];

  return log => {
    const ts = new Date().toISOString(); // UTC is more appropriate since other dates in the call are utc?

    regex.forEach(regex => regex.lastIndex = 0);

    for (const _regex of regex) {
      const match = _regex.exec(log)?.[0];
      if (match) {
        callback(ts + " " + match);
      }
    }
  };
}